// A photo, full screen, with the rest of the message's photos behind it.
// Lifted from the Andon app's `Lightbox`.
//
// WHY THIS EXISTS AT ALL, given a thumbnail is already inline.
//
// Because the thumbnail is capped at 240px so the conversation stays readable,
// and the thing somebody is actually looking at is whether the third bay is
// empty or whether that weld is cracked. The inline picture answers "is there a
// photo and roughly what of"; this answers the question the photo was taken to
// settle.
//
// It stays deliberately thin: open, look, swipe if there is more than one,
// close. No zoom gestures, no rotation, no editing — every one of those is a
// thing to get wrong on a gloved hand, and the browser's own full-screen image
// view is one tap further on for anybody who needs it.

import { useCallback, useEffect, useState } from 'react';
import { andonApi } from '@/api/andon/client';
import type { ThreadAttachmentVM } from '@/api/andon/types';
import { Icon } from '@/components/ui/Icon';
import { useT } from '@/i18n/useT';

export function Lightbox({
  alertId,
  items,
  startAt,
  onClose,
}: {
  alertId: number;
  items: ThreadAttachmentVM[];
  startAt: number;
  onClose: () => void;
}) {
  const t = useT();
  const [at, setAt] = useState(startAt);
  const count = items.length;
  const file = items[Math.min(at, count - 1)];

  const step = useCallback((by: number) => setAt((i) => Math.min(count - 1, Math.max(0, i + by))), [count]);

  /**
   * Escape closes, arrows page.
   *
   * On a tablet at a desk this is a keyboard-first surface, and trapping
   * somebody in a viewer they can only leave with the mouse is the kind of
   * thing that gets an app called clumsy.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, step]);

  /**
   * Hold the page still underneath.
   *
   * Without it, scrolling inside the viewer scrolls the thread behind it, and
   * closing lands somewhere other than where the photo was — which loses the
   * reader's place in a conversation they were part-way through.
   */
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  if (!file) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={file.name}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(5,6,9,.94)', display: 'flex', flexDirection: 'column' }}
    >
      <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', color: '#f3f5f7' }}>
        <span style={{ flex: 1, minWidth: 0, font: '600 12px Manrope,sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {file.name}
        </span>
        {count > 1 && (
          <span className="numeric" style={{ font: "700 11px 'Space Grotesk',sans-serif", opacity: 0.7 }}>
            {at + 1} / {count}
          </span>
        )}
        {/* Its own button as well as the backdrop. A tap-anywhere-to-close with
            no visible control is a thing people do not discover, and this
            opens over a stopped line. */}
        <button
          type="button"
          aria-label={t.closeT}
          onClick={onClose}
          style={{
            width: 36,
            height: 36,
            borderRadius: 12,
            border: 'none',
            background: 'rgba(255,255,255,.12)',
            color: '#f3f5f7',
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            flex: 'none',
          }}
        >
          <Icon name="cross" size={16} />
        </button>
      </div>

      <div
        // The image is NOT the backdrop's child for closing purposes: tapping
        // the picture itself should not dismiss it, or every attempt to look
        // closely closes the thing being looked at.
        onClick={(e) => e.stopPropagation()}
        style={{ flex: 1, minHeight: 0, display: 'grid', placeItems: 'center', padding: '0 12px 12px' }}
      >
        <img src={andonApi.attachmentUrl(alertId, file.id)} alt={file.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
      </div>

      {count > 1 && (
        <div onClick={(e) => e.stopPropagation()} style={{ flex: 'none', display: 'flex', justifyContent: 'center', gap: 12, padding: '0 0 22px' }}>
          <Step label={t.previousT} disabled={at === 0} onClick={() => step(-1)} />
          <Step label={t.nextT} flip disabled={at === count - 1} onClick={() => step(1)} />
        </div>
      )}
    </div>
  );
}

function Step({ label, flip, disabled, onClick }: { label: string; flip?: boolean; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 46,
        height: 46,
        borderRadius: 15,
        border: 'none',
        background: 'rgba(255,255,255,.12)',
        color: '#f3f5f7',
        display: 'grid',
        placeItems: 'center',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.35 : 1,
        transform: flip ? 'scaleX(-1)' : undefined,
      }}
    >
      <Icon name="back" size={18} />
    </button>
  );
}
